import { installWidgetTheme } from './generatedWidgetTheme';
import React, { useEffect, useMemo, useRef } from 'react';

export function widgetTheme() {
  const styles = getComputedStyle(document.documentElement);
  const names = ['--color-bg-base', '--color-bg-elevated', '--color-bg-floating', '--color-text-primary', '--color-text-secondary', '--color-border', '--color-primary', '--color-primary-subtle', '--color-primary-muted', '--color-text-muted', '--color-on-primary', '--color-bg-surface', '--color-control-bg', '--color-border-hover', '--color-border-active', '--color-danger', '--color-warning', '--color-success'];
  return { mode: document.documentElement.dataset.theme || 'light',
    tokens: Object.fromEntries(names.map(name => [name, styles.getPropertyValue(name).trim()])) };
}

const jsonForHtml = value => JSON.stringify(value).replace(/</g, '\\u003c');

export function widgetDocument(widget) {
  const nonce = 'spikescope-private-widget';
  return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'nonce-${nonce}'; style-src 'unsafe-inline'; img-src data:; connect-src 'none'; frame-src 'none'; object-src 'none'; base-uri 'none'; form-action 'none'; navigate-to 'none'">
<meta name="referrer" content="no-referrer"><style>html,body{margin:0;min-height:100%;font:14px system-ui;color:var(--color-text-primary,#18243a);background:var(--color-bg-base,#fff)}#root{padding:16px;box-sizing:border-box;overflow:auto}*{box-sizing:border-box}button,input,select{font:inherit}</style></head><body><div id="root"></div>
<script nonce="${nonce}">
(function(){
  const root=document.getElementById('root');
  const applyTheme=(${installWidgetTheme.toString()})();
  const report=(ok,message)=>parent.postMessage({type:'spikescope-widget-result',ok,message},'*');
  window.addEventListener('error',()=>report(false,'Widget code failed. Ask the assistant to revise it.'));
  const style=document.createElement('style');style.textContent=${jsonForHtml(widget.css || '')};document.head.appendChild(style);
  const code=document.createElement('script');code.nonce='${nonce}';code.textContent=${jsonForHtml(widget.source || '')};document.head.appendChild(code);
  // Cover control-driven redraws as well as incoming data updates.
  if(typeof render==='function'){
    const renderWidget=render;
    render=function(target,data){target.replaceChildren();return renderWidget(target,data);};
  }
  window.addEventListener('message',event=>{
    if(event.source!==parent || event.data?.type!=='spikescope-widget-data')return;
    const theme=event.data.data?.theme;
    if(theme)applyTheme(theme);
    try{root.replaceChildren();if(typeof render!=='function')throw new Error('Missing render');render(root,event.data.data);report(true,'Preview rendered');}
    catch(error){root.textContent='This widget could not render this data. Ask the assistant to revise it.';report(false,root.textContent);}
  });
})();
</script></body></html>`;
}

export default function GeneratedWidget({ widget, data, onResult }) {
  const frame = useRef(null);
  const source = useMemo(() => widgetDocument(widget), [widget]);
  const currentData = useRef(data);
  currentData.current = data;
  const send = () => frame.current?.contentWindow?.postMessage({ type: 'spikescope-widget-data', data: { ...currentData.current, theme: widgetTheme() } }, '*');
  useEffect(() => {
    const listener = event => {
      if (event.source === frame.current?.contentWindow && event.data?.type === 'spikescope-widget-result') {
        onResult?.({ ok: event.data.ok === true, message: String(event.data.message || '').slice(0, 300) });
      }
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [onResult]);
  useEffect(() => {
    const sendTheme = () => frame.current?.contentWindow?.postMessage({ type: 'spikescope-widget-data', data: { ...currentData.current, theme: widgetTheme() } }, '*');
    sendTheme();
    const observer = new MutationObserver(sendTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'style', 'class'] });
    return () => observer.disconnect();
  }, [data]);
  return <iframe ref={frame} title={widget.name} sandbox="allow-scripts" referrerPolicy="no-referrer"
    srcDoc={source} onLoad={send} style={{ width: '100%', height: '100%', minHeight: 260, border: 0, background: 'var(--color-bg-base)'  }} />;
}

// Deliberately bounded, read-only data bridge: never pass session credentials or callbacks.
export function generatedWidgetData({ clusters = [], selectedClusters = [], clusterStats = {}, datasetInfo = {}, selectedAlgorithm, focusedTimeRange }) {
  const metricKeys = ['spike_count', 'spikeCount', 'firing_rate', 'firingRate', 'isi_violation_rate', 'isiViolations', 'amplitude', 'snr'];
  return {
    clusters: clusters.slice(0, 500).map(c => ({ clusterId: c.clusterId ?? c.id,
      spikeCount: c.spikeCount ?? c.spike_count ?? c.count ?? c.size })),
    selectedClusters: selectedClusters.slice(0, 500),
    clusterStats: Object.fromEntries(Object.entries(clusterStats || {}).slice(0, 500).map(([id, value]) => [id,
      Object.fromEntries(metricKeys.filter(k => typeof value?.[k] === 'number' && Number.isFinite(value[k])).map(k => [k, value[k]]))])),
    datasetInfo: { sampleRate: datasetInfo?.sampleRate ?? datasetInfo?.sampling_rate,
      duration: datasetInfo?.duration },
    selectedAlgorithm: typeof selectedAlgorithm === 'string' ? selectedAlgorithm : '',
    focusedTimeRange: Array.isArray(focusedTimeRange) ? focusedTimeRange.slice(0, 2) : null,
  };
}
